import React, { useEffect, useState } from 'react';
import { NCZIEntry } from '../../../../Interfaces/ncziTypes';
import EHREntryInfo from '../EHREntry/EHREntryInfo';
import EHREntryModal from './EHREntryModal';
import LinkButton from '../../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../../Enums/Theme.enum';
import { joinClasses } from '../../../../../../Utils/className.util';
import Tag from '../../../../../../Components/Tag/Tag';
import Text from '../../../../../../Components/Base/Text/Text';
import ElectronicVaccinationCreateReactionModal from '../../../ElectronicVaccination/CreateReaction/ElectronicVaccinationCreateReactionModal';
import moment from 'moment/moment';
import useTranslation from '../../../../../../Hooks/useTranslation';
import ElectronicDPNChangeModal from '../../../ElectronicDPN/Change/ElectronicDPNChangeModal';

// @ts-ignore
import styles from './EHROpenableEntry.module.scss';

const AT_EX_NAME =
	'CEN-EN13606-ENTRY.Zaznam_o_vysetreni-Zaznam_o_odbornom_vysetreni.v7/at0.34';
const AT_PN = 'CEN-EN13606-ENTRY.PotvrdenieDocasnejPraceneschopnosti.v1/at0000';
const AT_PN_DATE_FROM =
	'CEN-EN13606-ENTRY.PotvrdenieDocasnejPraceneschopnosti.v1/at0002';
const AT_PN_DATE_TO =
	'CEN-EN13606-ENTRY.PotvrdenieDocasnejPraceneschopnosti.v1/at0003';
const AT_VACCINATION = 'CEN-EN13606-SECTION.Ockovaci_zaznam.v1/at0001.3';

interface Props {
	item: NCZIEntry | undefined;
	patientIdentifier: string;
	onCloseDetail?: (needReload: boolean) => void;
}

const EHROpenableEntry: React.FC<Props> = props => {
	const { item, patientIdentifier, onCloseDetail } = props;
	const [entryOpened, setEntryOpened] = useState<boolean>(false);
	const [
		electronicVaccinationCreateReactionOpen,
		setElectronicVaccinationCreateReactionOpen,
	] = useState<boolean>(false);
	const [electronicDPNEndOpen, setElectronicDPNEndOpen] =
		useState<boolean>(false);
	const [entryTitle, setEntryTitle] = useState<string>(item?.name || '');
	const { t } = useTranslation();
	const [hasDateTo, setHasDateTo] = useState<boolean>(false);

	if (!item) {
		return <></>;
	}

	useEffect(() => {
		let newName = '';
		let dateFrom = undefined;
		let dateTo = undefined;

		for (const entryItem of item.items) {
			if (entryItem.element) {
				if (AT_EX_NAME === entryItem.element.archetypeId) {
					newName = entryItem.element.value;
				} else if (AT_PN_DATE_FROM === entryItem.element.archetypeId) {
					dateFrom = moment(entryItem.element.value).format('DD.MM.YYYY');
				} else if (AT_PN_DATE_TO === entryItem.element.archetypeId) {
					dateTo = moment(entryItem.element.value).format('DD.MM.YYYY');
					setHasDateTo(true);
				}
			}
		}

		if (dateFrom) {
			newName = dateFrom + ' - ' + (dateTo || t('nczi.electronicDPN.list.now'));
		}

		if (newName) {
			setEntryTitle(newName);
		}
	}, []);

	const handleCloseDetailModal = (needReload: boolean = false) => {
		setEntryOpened(false);
		onCloseDetail?.(needReload);
	};

	const handleCloseElectronicVaccinationCreateReactionModal = (
		needReload: boolean = false,
	) => {
		setElectronicVaccinationCreateReactionOpen(false);
		onCloseDetail?.(needReload);
	};

	const handleCloseElectronicDPNEndModalModal = (
		needReload: boolean = false,
	) => {
		setElectronicDPNEndOpen(false);
		onCloseDetail?.(needReload);
	};

	return (
		<>
			<div
				className={joinClasses([
					styles.wrapper,
					item.info?.version === 'VER04' ? styles.cancelled : undefined,
				])}>
				<div className={styles.border}>
					<div className={styles.content}>
						<div className={styles.header}>
							<div className={styles.openEntryName}>
								{item.info?.version === 'VER04' ? (
									<>
										<Text tx={entryTitle} className={styles.cancelledTitle} />
										<Tag
											label={'nczi.cancelled'}
											className={styles.cancelledTag}
										/>
									</>
								) : (
									entryTitle
								)}
							</div>
						</div>
						<EHREntryInfo info={item.info} />
					</div>
					<div className={styles.controls}>
						<LinkButton
							title={'nczi.showDetails'}
							theme={Theme.BLUE}
							onClick={() => setEntryOpened(true)}
						/>
						{item.archetypeId === AT_VACCINATION &&
							item.info?.version !== 'VER04' && (
								<LinkButton
									firstUpper
									title={'nczi.addVaccinationReaction'}
									theme={Theme.RED}
									onClick={() =>
										setElectronicVaccinationCreateReactionOpen(true)
									}
								/>
							)}
						{item.archetypeId === AT_PN &&
							!hasDateTo &&
							item.info?.version !== 'VER04' && (
								<LinkButton
									firstUpper
									title={'nczi.endOrChangeDPN'}
									theme={Theme.RED}
									onClick={() => setElectronicDPNEndOpen(true)}
								/>
							)}
					</div>
				</div>
			</div>
			<EHREntryModal
				opened={entryOpened}
				onClose={handleCloseDetailModal}
				entry={item}
				patientIdentifier={patientIdentifier}
			/>
			{item.recordId && (
				<ElectronicVaccinationCreateReactionModal
					opened={electronicVaccinationCreateReactionOpen}
					onClose={handleCloseElectronicVaccinationCreateReactionModal}
					patientIdentifier={patientIdentifier}
					vaccinationId={item.recordId}
				/>
			)}
			{item.recordId && (
				<ElectronicDPNChangeModal
					opened={electronicDPNEndOpen}
					onClose={handleCloseElectronicDPNEndModalModal}
					patientIdentifier={patientIdentifier}
					electronicConfirmation={item}
				/>
			)}
		</>
	);
};

export default EHROpenableEntry;
